import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useClickOutside } from '../../utils/useClickOutside';
import styles from './ProjectTabs.module.scss';
import { ReactComponent as FolderIcon } from './assets/folder.svg';
import { ReactComponent as FolderGearIcon } from './assets/folderGear.svg';
import { Tooltip } from 'react-tooltip';
import { ReactComponent as AddIcon } from '../../assets/plus.svg';
import { cutString } from '../../utils/cutString';

interface Project {
  id: number;
  name: string;
  case_ids?: number[];
  order?: number;
}

interface ProjectTabsProps {
  projects: Project[];
  activeTabId: number | null;
  onTabChange: (id: number | null) => void;
  onCreateProject: () => void;
  onManageProjects: () => void;
}

const ALL_CASES_ID = null;

export function ProjectTabs({
  projects,
  activeTabId,
  onTabChange,
  onCreateProject,
  onManageProjects,
}: ProjectTabsProps) {
  const { t } = useTranslation();

  const [visibleCount, setVisibleCount] = useState(projects.length);
  const [overrideLastVisible, setOverrideLastVisible] = useState<Project | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const allCasesRef = useRef<HTMLButtonElement>(null);
  const manageRef = useRef<HTMLButtonElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  useClickOutside(overflowRef, () => setDropdownOpen(false));

  // Reset override when the project list changes (e.g. after manage/reorder)
  useEffect(() => {
    setOverrideLastVisible(null);
  }, [projects]);

  const recalculate = useCallback(() => {
    if (!containerRef.current || !measureRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const manageWidth = (manageRef.current?.offsetWidth ?? 36) + 8;
    const allCasesWidth = (allCasesRef.current?.offsetWidth ?? 90) + 8;
    const moreTabEst = 80; // approximate width of "N more…" tab

    const available = containerWidth - manageWidth - allCasesWidth;

    const tabEls = Array.from(measureRef.current.children) as HTMLElement[];
    if (tabEls.length === 0) {
      setVisibleCount(0);
      return;
    }

    // Check if all tabs fit without a "more" tab
    const totalWidth = tabEls.reduce((sum, el) => sum + el.offsetWidth + 8, 0);
    if (totalWidth <= available) {
      setVisibleCount(tabEls.length);
      return;
    }

    // Find how many fit when we also need to reserve space for the "more" tab
    let count = 0;
    let used = 0;
    for (const el of tabEls) {
      const w = el.offsetWidth + 8;
      if (used + w + moreTabEst + 8 <= available) {
        used += w;
        count++;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  }, []);

  useLayoutEffect(() => {
    recalculate();
    if (!containerRef.current) return;
    const observer = new ResizeObserver(recalculate);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [projects, recalculate]);

  // ── Compute visible / hidden ──────────────────────────────────────────────
  const naturalVisible = projects.slice(0, visibleCount);
  const naturalHidden = projects.slice(visibleCount);
  const hasOverflow = naturalHidden.length > 0;

  let visibleTabs = naturalVisible;
  let hiddenTabs = naturalHidden;

  if (overrideLastVisible && hasOverflow) {
    const displaced = naturalVisible[naturalVisible.length - 1];
    visibleTabs = [...naturalVisible.slice(0, -1), overrideLastVisible];
    hiddenTabs = [
      ...(displaced && displaced.id !== overrideLastVisible.id ? [displaced] : []),
      ...naturalHidden.filter((p) => p.id !== overrideLastVisible.id),
    ];
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTabClick = (id: number | null) => {
    if (overrideLastVisible && id !== overrideLastVisible.id) {
      setOverrideLastVisible(null);
    }
    onTabChange(id);
  };

  const handleOverflowSelect = (project: Project) => {
    setOverrideLastVisible(project);
    onTabChange(project.id);
    setDropdownOpen(false);
  };

  // ── No projects: "Create project" button ────────────────────────────────
  if (projects.length === 0) {
    return (
      <div className={styles.toolbar}>
        <button type="button" className={styles.createButton} onClick={onCreateProject}>
          <AddIcon aria-hidden="true" />
          {t('Create project')}
        </button>
      </div>
    );
  }

  // ── With projects: manage button + tabs ──────────────────────────────────
  return (
    <div className={styles.toolbar} ref={containerRef}>
      {/* Manage projects */}
      <button
        ref={manageRef}
        type="button"
        className={styles.manageButton}
        onClick={onManageProjects}
        title={t('Manage projects')}
        aria-label={t('Manage projects')}
        data-tooltip-id="manage-projects"
        data-tooltip-content={t('Manage projects')}
      >
        <FolderGearIcon />
      </button>
      <Tooltip id="manage-projects" place="bottom" style={{ zIndex: 300 }} />

      {/* All Cases */}
      <button
        ref={allCasesRef}
        type="button"
        className={cn(styles.tab, { [styles.tabSelected]: activeTabId === ALL_CASES_ID })}
        onClick={() => handleTabClick(ALL_CASES_ID)}
        aria-pressed={activeTabId === ALL_CASES_ID}
      >
        {t('All cases')}
      </button>

      {/* Visible project tabs */}
      {visibleTabs.map((project) => {
        const isTruncated = project.name.length > 26;
        return (
          <button
            key={project.id}
            type="button"
            className={cn(styles.tab, styles.tabWithIcon, {
              [styles.tabSelected]: activeTabId === project.id,
            })}
            onClick={() => handleTabClick(project.id)}
            aria-pressed={activeTabId === project.id}
            {...(isTruncated && {
              'data-tooltip-id': `project-${project.id}`,
              'data-tooltip-content': project.name,
            })}
          >
            <FolderIcon />
            <span>{cutString(project.name, 26)}</span>
            {isTruncated && <Tooltip id={`project-${project.id}`} place="bottom" style={{ zIndex: 300, maxWidth: '300px', whiteSpace: 'wrap' }} />}
          </button>
        );
      })}

      {/* Overflow "N more…" tab + dropdown */}
      {hasOverflow && (
        <div className={styles.overflowWrap} ref={overflowRef}>
          <button
            type="button"
            className={cn(styles.tab, { [styles.tabMore]: dropdownOpen })}
            onClick={() => setDropdownOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            {/* {hiddenTabs.length}&nbsp;{t('more')} */}
            {t('showMoreProjects', { count: hiddenTabs.length })}
          </button>

          {dropdownOpen && (
            <ul className={styles.dropdown} role="listbox" aria-label={t('More projects')}>
              {hiddenTabs.map((project) => (
                <li key={project.id} role="option" aria-selected={activeTabId === project.id}>
                  <button
                    type="button"
                    className={cn(styles.dropdownItem, {
                      [styles.dropdownItemActive]: activeTabId === project.id,
                    })}
                    onClick={() => handleOverflowSelect(project)}
                  >
                    <span className={styles.projectIcon}>
                      <FolderIcon />
                    </span>
                    
                    <span className={styles.projectName}>{project.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Hidden measurement container — renders all project tabs off-screen */}
      <div className={styles.measureContainer} ref={measureRef} aria-hidden="true">
        {projects.map((project) => (
          <div key={project.id} className={cn(styles.tab, styles.tabWithIcon)}>
            <FolderIcon />
            <span>{cutString(project.name, 26)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
